import { useCallback } from "react";
import type {
  WorkspaceState,
  WorkspaceCallbacks,
  SelectionOperations,
  SelectionBounds,
} from "../../utils/workspace.types";
import * as THREE from "three";
import {
  projectToScreen,
  isCompletelyContained,
} from "../../utils/selectionUtils";
import { useModelStore } from "../../../../store/useModelStore";

/**
 * Selection operations hook - handles drag selection functionality
 *
 * Features:
 * 1. Ctrl+Drag: Immediate drag selection with selection mode filter (nodes/beams/all)
 * 2. STAAD.Pro style: Complete containment selection (elements must be fully inside rectangle)
 * 3. Mode-aware selection: Strictly respects NODE/BEAM/ALL filter from ViewMenu
 *
 * Extracted from STAADWorkspace3D for modularity
 */
export const useSelectionOperations = (
  state: WorkspaceState,
  callbacks: WorkspaceCallbacks,
  modelStore: any,
  uiStore: any,
  isBoxSelectionEnabled: () => boolean
): SelectionOperations => {
  // ---------- Camera / canvas helpers ----------
  const getCamera = (): THREE.Camera => {
    if (modelStore?.camera) return modelStore.camera as THREE.Camera;
    if ((window as any).__workspaceCamera)
      return (window as any).__workspaceCamera as THREE.Camera;

    const fallback = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    fallback.position.set(57.735, 57.735, 57.735);
    fallback.lookAt(0, 0, 0);
    return fallback;
  };

  const buildCanvasRect = (): DOMRect => {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
      left: 0,
      top: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
    } as DOMRect;
  };

  // ---------- Node selection helper ----------
  const selectNodesInBounds = (
    minX: number,
    maxX: number,
    minY: number,
    maxY: number,
    camera: THREE.Camera,
    canvasRect: DOMRect
  ) => {
    const ids: string[] = [];
    if (!modelStore?.nodes) return ids;

    const nodeRadius = 8;
    const selectionBounds = { minX, maxX, minY, maxY };

    for (const node of modelStore.nodes) {
      const world = new THREE.Vector3(node.x, node.y, node.z);
      const screen = projectToScreen(world, camera, canvasRect);

      const elementBounds = {
        left: screen.x - nodeRadius,
        right: screen.x + nodeRadius,
        top: screen.y - nodeRadius,
        bottom: screen.y + nodeRadius,
      };

      if (isCompletelyContained(elementBounds, selectionBounds)) {
        ids.push(node.id);
      }
    }

    return ids;
  };

  // ---------- Beam selection helper ----------
  const selectBeamsInBounds = (
    minX: number,
    maxX: number,
    minY: number,
    maxY: number,
    camera: THREE.Camera,
    canvasRect: DOMRect
  ) => {
    const ids: string[] = [];
    if (!modelStore?.beams || !modelStore?.nodes) return ids;

    const beamThickness = 4;
    const selectionBounds = { minX, maxX, minY, maxY };

    // fast lookup
    const nodeById = new Map<string, any>();
    for (const n of modelStore.nodes) nodeById.set(n.id, n);

    for (const beam of modelStore.beams) {
      const startNode = nodeById.get(beam.startNodeId);
      const endNode = nodeById.get(beam.endNodeId);
      if (!startNode || !endNode) continue;

      const startWorld = new THREE.Vector3(
        startNode.x,
        startNode.y,
        startNode.z
      );
      const endWorld = new THREE.Vector3(endNode.x, endNode.y, endNode.z);

      const startScreen = projectToScreen(startWorld, camera, canvasRect);
      const endScreen = projectToScreen(endWorld, camera, canvasRect);

      const elementBounds = {
        left: Math.min(startScreen.x, endScreen.x) - beamThickness,
        right: Math.max(startScreen.x, endScreen.x) + beamThickness,
        top: Math.min(startScreen.y, endScreen.y) - beamThickness,
        bottom: Math.max(startScreen.y, endScreen.y) + beamThickness,
      };

      if (isCompletelyContained(elementBounds, selectionBounds)) {
        ids.push(beam.id);
      }
    }

    return ids;
  };

  // ---------- Main selection function ----------
  const selectElementsInBounds = useCallback(
    (bounds: { start: SelectionBounds; end: SelectionBounds }) => {
      // Calculate rectangle
      const minX = Math.min(bounds.start.x, bounds.end.x);
      const maxX = Math.max(bounds.start.x, bounds.end.x);
      const minY = Math.min(bounds.start.y, bounds.end.y);
      const maxY = Math.max(bounds.start.y, bounds.end.y);

      // small selection guard
      if (Math.abs(maxX - minX) < 10 || Math.abs(maxY - minY) < 10) return;

      // Get selection filter from global mode (set by ViewMenu) - STRICT filter
      const globalMode = (window as any).__currentSelectionMode;
      const effectiveFilter: "all" | "nodes" | "beams" = globalMode || "all";

      // If there is an enhanced, camera-aware function on window, prefer it.
      if ((window as any).selectElementsInBoundsWithCamera) {
        // Always use additive selection (Ctrl+drag adds to existing)
        (window as any).selectElementsInBoundsWithCamera(
          minX,
          maxX,
          minY,
          maxY,
          true,
          effectiveFilter
        );
        return;
      }

      const camera = getCamera();
      const canvasRect = buildCanvasRect();

      // STRICT filtering based on mode
      const wantNodes =
        effectiveFilter === "all" || effectiveFilter === "nodes";
      const wantBeams =
        effectiveFilter === "all" || effectiveFilter === "beams";

      // Only select the element types allowed by the filter
      const newSelectedNodeIds = wantNodes
        ? selectNodesInBounds(minX, maxX, minY, maxY, camera, canvasRect)
        : [];
      const newSelectedBeamIds = wantBeams
        ? selectBeamsInBounds(minX, maxX, minY, maxY, camera, canvasRect)
        : [];

      // Get current selection
      const currentNodeIds = modelStore?.selectedNodeIds || [];
      const currentBeamIds = modelStore?.selectedBeamIds || [];

      // Calculate final beam selection
      let finalBeamIds: string[] = [];
      if (wantBeams) {
        // Merge beams
        finalBeamIds = [...new Set([...currentBeamIds, ...newSelectedBeamIds])];
      } else {
        // Keep existing beams unchanged (filter is nodes-only)
        finalBeamIds = currentBeamIds;
      }

      // Calculate final node selection - STRICTLY respect mode
      let finalNodeIds: string[] = [];
      if (effectiveFilter === "nodes") {
        // NODES ONLY mode - only select nodes, don't add beam endpoints
        finalNodeIds = [...new Set([...currentNodeIds, ...newSelectedNodeIds])];
      } else if (effectiveFilter === "beams") {
        // BEAMS ONLY mode - keep existing nodes unchanged, don't auto-add endpoints
        finalNodeIds = currentNodeIds;
      } else {
        // ALL mode - select both and include beam endpoints for visual consistency
        const beamEndpointNodes = new Set<string>();
        finalBeamIds.forEach((beamId) => {
          const beam = modelStore?.beams?.find((b: any) => b.id === beamId);
          if (beam) {
            beamEndpointNodes.add(beam.startNodeId);
            beamEndpointNodes.add(beam.endNodeId);
          }
        });
        finalNodeIds = [
          ...new Set([
            ...currentNodeIds,
            ...newSelectedNodeIds,
            ...Array.from(beamEndpointNodes),
          ]),
        ];
      }

      // Apply selection using direct setState to bypass auto-endpoint behavior
      useModelStore.setState((state: any) => ({
        selectedBeamIds: finalBeamIds,
        selectedNodeIds: finalNodeIds,
        beams: state.beams.map((beam: any) => ({
          ...beam,
          isSelected: finalBeamIds.includes(beam.id),
        })),
        nodes: state.nodes.map((node: any) => ({
          ...node,
          isSelected: finalNodeIds.includes(node.id),
          color: finalNodeIds.includes(node.id) ? "#ff8c00" : "#000000",
        })),
      }));

      const finalSelectionBounds = { minX, maxX, minY, maxY };
      if (newSelectedNodeIds.length > 0 || newSelectedBeamIds.length > 0) {
        (window as any).__lastSelectionBounds = finalSelectionBounds;
      }

      // notification
      if (uiStore?.showNotification) {
        const total = newSelectedNodeIds.length + newSelectedBeamIds.length;
        if (total > 0) {
          const filterMsg =
            effectiveFilter === "all" ? "" : ` (${effectiveFilter} only)`;
          uiStore.showNotification(
            `Added ${newSelectedNodeIds.length} nodes and ${newSelectedBeamIds.length} beams to selection${filterMsg}`,
            "success"
          );
        } else {
          uiStore.showNotification(
            `No ${effectiveFilter} in selection area`,
            "info"
          );
        }
      }
    },
    [modelStore, uiStore, state.selectionFilter, state.isCtrlPressed]
  );

  // ---------- Event handlers ----------

  // NOTE: handleCanvasDoubleClick has been REMOVED
  // Selection mode is now controlled exclusively via ViewMenu buttons (Nodes/Beams/All/Clear)
  // Ctrl+Drag is the only way to perform rectangle selection

  /**
   * Handle mouse down for drag selection
   * Works when: Selection mode is active OR Ctrl/Cmd key is held
   */
  const handleCanvasMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!isBoxSelectionEnabled()) {
        console.log("🚫 Box selection disabled - table apply mode active");
        return;
      }
      // Check if Ctrl/Cmd is pressed (allows Ctrl+Drag without selection mode)
      const isCtrl = event.ctrlKey || event.metaKey;

      // Allow selection if: selection mode is ON, OR Ctrl is held
      if (!state.isSelectionMode && !isCtrl) return;

      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const startPoint = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };

      callbacks.setSelectionStart(startPoint);
      callbacks.setSelectionEnd(startPoint);
      callbacks.setIsDraggingSelection(true);

      // Update Ctrl state for additive selection
      if (isCtrl) {
        callbacks.setIsCtrlPressed(true);
      }
    },
    [state.isSelectionMode, callbacks, isBoxSelectionEnabled]
  );

  /**
   * Handle mouse move for updating selection rectangle
   */
  const handleCanvasMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!state.isDraggingSelection || !state.selectionStart) return;

      const rect = (event.target as HTMLElement).getBoundingClientRect();
      const currentPoint = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };

      callbacks.setSelectionEnd(currentPoint);
    },
    [state.isDraggingSelection, state.selectionStart, callbacks]
  );

  /**
   * Handle mouse up for finalizing selection
   */
  const handleCanvasMouseUp = useCallback(
    (event: React.MouseEvent) => {
      if (
        !state.isDraggingSelection ||
        !state.selectionStart ||
        !state.selectionEnd
      ) {
        callbacks.setIsDraggingSelection(false);
        callbacks.setSelectionStart(null);
        callbacks.setSelectionEnd(null);
        return;
      }

      const bounds = {
        start: state.selectionStart,
        end: state.selectionEnd,
      };

      selectElementsInBounds(bounds);

      callbacks.setIsDraggingSelection(false);
      callbacks.setSelectionStart(null);
      callbacks.setSelectionEnd(null);
    },
    [
      state.isDraggingSelection,
      state.selectionStart,
      state.selectionEnd,
      callbacks,
      selectElementsInBounds,
    ]
  );

  // ---------- exported operations ----------
  return {
    handleCanvasMouseDown,
    handleCanvasMouseMove,
    handleCanvasMouseUp,
    selectElementsInBounds,
  };
};
